import { useEffect, useReducer } from "react"

type ImgLoadingState = "loading" | "loaded" | "error";

// Cache: URL -> loading state
const loadStates: Record<string, ImgLoadingState> = {}

// Pub/Sub: listener per URL
const listeners: Map<string, Set<() => void>> = new Map()

function subscribe(url: string, callback: () => void) {
  let set = listeners.get(url)
  if (!set) {
    set = new Set()
    listeners.set(url, set)
  }
  set.add(callback)

  return () => {
    set!.delete(callback)
    if (set!.size === 0) {
      listeners.delete(url)
    }
  }
}

function notify(url: string) {
  listeners.get(url)?.forEach((cb) => cb())
}

function startPreload(url: string) {
  if (url in loadStates) { return }

  loadStates[url] = "loading"

  const img = new Image()
  img.onload = () => {
    loadStates[url] = "loaded"
    notify(url)
  }
  img.onerror = () => {
    loadStates[url] = "error"
    notify(url)
  }
  img.src = url
}

export const usePreloadImages = (imageUrls: string[]) => {
  // Stabiler Dependency-Key, falls das Array bei jedem Render neu erzeugt wird
  const urlsKey = imageUrls.join("|")

  const [, forceRender] = useReducer((c) => c + 1, 0)

  useEffect(() => {
    // Preload für alle (noch nicht gestarteten) URLs anstoßen
    imageUrls.forEach(startPreload)

    // Auf Änderungen aller relevanten URLs subscriben
    const unsubscribers = imageUrls.map((url) => subscribe(url, forceRender))

    return () => {
      unsubscribers.forEach((unsub) => unsub())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlsKey])

  let numPreloaded = 0
  let numError = 0

  for (const url of imageUrls) {
    const state = loadStates[url]
    if (state === "loaded") { numPreloaded++ }
    else if (state === "error") { numError++ }
  }

  const numTotalToPreload = imageUrls.length
  const preloadingDone = numPreloaded + numError === numTotalToPreload

  return {
    numPreloaded,
    numError,
    numTotalToPreload,
    preloadingDone,
  }
}
